import ajax from '@/lib/ajax';
import store from '@/store';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import { AppModule } from '@/store/Auth/AppModule';
import GeneralLedgerAccount from '@/models/Accounting/GeneralLedgerAccounts/GeneralLedgerAccount';
import CreateGeneralLedgerAccountRequest from '@/models/Accounting/GeneralLedgerAccounts/CreateGeneralLedgerAccountRequest';
import UpdateGeneralLedgerAccountRequest from '@/models/Accounting/GeneralLedgerAccounts/UpdateGeneralLedgerAccountRequest';
import GeneralLedgerAccountState from "@/models/Accounting/GeneralLedgerAccounts/GeneralLedgerAccountState";
import JournalEntry from '@/models/Accounting/JournalEntries/JournalEntry';
import PermissionUtils from '@/lib/permissionUtils';

// Hardcoded for now until we can get chart of accounts from the API
const testJournalEntries = [
    {
        id: "ee445e46-5c7c-4d24-ad23-d89cd9eaf136",
        date: "01/03/2023",
        eventType: "CREDIT_RECEIVED",
        eventId: "ee445e46-5c7c-4d24-ad23-d89cd9eaf136",
        transactionType: "FPS_INBOUND_PAYMENT",
        transactionId: "ee445e46-5c7c-4d24-ad23-d89cd9eaf136",
        subTransactionType: "",
        transactionAmount: "",
        createdAt: new Date(),
        bookingDate: new Date(),
        valueDate: new Date(),
        productId: "64d5bc2f-de13-4471-9cf8-9cbeb9efb93f",
        productIssueId: "8891fa0f-44c9-4f99-a7f5-ea88a91eb745",
        productType: "Deposit",
        productSubType: "Current Account",
        debitAccount: "ABC Bank Clearbank Seg Account (100021)",
        debitAmount: "15,000 GBP",
        creditAccount: "SaaScada Deposit Accounts (200090)",
        creditAmount: "15,000 GBP",
        notes: "Some notes about this Journal Entry",
    },
    {
        id: "d29768dc-cf35-4e3c-a5e0-a5eb8a6798f0",
        date: "01/03/2023",
        eventType: "INTEREST_ACCRUAL",
        eventId: "d29768dc-cf35-4e3c-a5e0-a5eb8a6798f0",
        transactionType: "DEPOSIT_FIXED_TERM_INTEREST",
        transactionId: "ee445e46-5c7c-4d24-ad23-d89cd9eaf136",
        subTransactionType: "",
        transactionAmount: "",
        createdAt: new Date(),
        bookingDate: new Date(),
        valueDate: new Date(),
        productId: "64d5bc2f-de13-4471-9cf8-9cbeb9efb93f",
        productIssueId: "8891fa0f-44c9-4f99-a7f5-ea88a91eb745",
        productType: "Deposit",
        productSubType: "Current Account",
        debitAccount: "Deposit Interest Payable SaaScada Accounts (400309)",
        debitAmount: "1.50 GBP",
        creditAccount: "SaaScada Interest Payable (200092)",
        creditAmount: "1.50 GBP",
        notes: "Some notes about this Journal Entry",
    },
]

export interface IGeneralLedgerAccountsState {
    loadingGeneralLedgerAccounts: boolean;
    loadingGeneralLedgerAccount: boolean;
    loadingNewGeneralLedgerAccount: boolean;
    generalLedgerAccounts: GeneralLedgerAccount[];
    generalLedgerAccountsForDialog: GeneralLedgerAccount[];
    totalGeneralLedgerAccountsPages: number;
    generalLedgerAccount: GeneralLedgerAccount;
    newGeneralLedgerAccountID: string;
    generalLedgerAccountJournalEntries: JournalEntry[];
    totalGeneralLedgerAccountJournalEntriesPages: number;
    filterByAccountState: GeneralLedgerAccountState;
    pageSize: number;
}

@Module({ dynamic: true, store, name: 'GeneralLedgerAccountsModule', namespaced: true })
class GeneralLedgerAccountsStore extends VuexModule implements IGeneralLedgerAccountsState {

    public loadingGeneralLedgerAccounts: boolean = true;
    public loadingGeneralLedgerAccount: boolean = false;
    public loadingNewGeneralLedgerAccount: boolean = false;
    public generalLedgerAccounts: GeneralLedgerAccount[] = [];
    public generalLedgerAccountsForDialog: GeneralLedgerAccount[] = [];
    public totalGeneralLedgerAccountsPages: number = 1;
    public generalLedgerAccount: GeneralLedgerAccount = new GeneralLedgerAccount();
    public newGeneralLedgerAccountID: string = null;
    public generalLedgerAccountJournalEntries: JournalEntry[] = [];
    public totalGeneralLedgerAccountJournalEntriesPages: number = 1;
    public filterByAccountState: GeneralLedgerAccountState = null;
    public pageSize: number = PermissionUtils.getPageSize();

    @Action
    async getGeneralLedgerAccounts(pageNumber: number) {

        this.setLoadingGeneralLedgerAccounts(true);

        const stateParameter = this.filterByAccountState ? `&state=${this.filterByAccountState}` : ``;

        await ajax.get(`/portal-backend/fi-api/internal/general-ledger-accounts?pageSize=${this.pageSize}&pageNumber=${pageNumber}${stateParameter}`)
        .then(response => {
            this.context.commit("setGeneralLedgerAccounts", response.data.results);
            this.context.commit("setGeneralLedgerAccountsTotalPages", response.data.records.totalRecords);
            this.setLoadingGeneralLedgerAccounts(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingGeneralLedgerAccounts(false);
        });
    }

    @Action
    async getGeneralLedgerAccountsForDialog() {

        // This action is specifically for the edit/create dialogs where we need to ignore the normal API pagination
        // logic in getGeneralLedgerAccounts and instead get all accounts so they can be listed in the drop down menu for dialogs
        this.setLoadingGeneralLedgerAccounts(true);

        await ajax.get(`/portal-backend/fi-api/internal/general-ledger-accounts`)
        .then(response => {
            this.context.commit("setGeneralLedgerAccountsForDialog", response.data.results);
            this.setLoadingGeneralLedgerAccounts(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingGeneralLedgerAccounts(false);
        });
    }

    @Action
    async getGeneralLedgerAccount(id: string) {

        this.setLoadingGeneralLedgerAccount(true);

        await ajax.get(`/portal-backend/fi-api/internal/general-ledger-accounts/${id}`)
        .then(response => {
            this.context.commit("setGeneralLedgerAccount", response.data);
            this.setLoadingGeneralLedgerAccount(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingGeneralLedgerAccount(false);
        });
    }

    @Action
    public async createGeneralLedgerAccount(request: CreateGeneralLedgerAccountRequest) {

        this.setLoadingNewGeneralLedgerAccount(true);

        await ajax.post(`/portal-backend/fi-api/internal/general-ledger-accounts/`, request)
        .then(response => {
            this.context.commit("setNewGeneralLedgerAccountID", response.data.id);
            this.setLoadingNewGeneralLedgerAccount(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingNewGeneralLedgerAccount(false);
        });
    }

    @Action
    public async editGeneralLedgerAccount(request: UpdateGeneralLedgerAccountRequest) {

        this.setLoadingGeneralLedgerAccount(true);

        await ajax.patch(`/portal-backend/fi-api/internal/general-ledger-accounts/${this.generalLedgerAccount.id}`, request)
        .then(response => {
            this.context.commit("setGeneralLedgerAccount", response.data);
            this.setLoadingGeneralLedgerAccount(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingGeneralLedgerAccount(false);
        });
    }

    @Action
    async getGeneralLedgerAccountJournalEntries(id: string) {

        this.context.commit("setGeneralLedgerAccountJournalEntries", testJournalEntries);
        this.context.commit("setGeneralLedgerAccountJournalEntriesTotalPages", testJournalEntries.length);

        // this.setLoadingGeneralLedgerAccount(true);

        // await ajax.get(`/portal-backend/fi-api/accounting/general-ledger-accounts/${id}`)
        // .then(response => {
        //     this.setLoadingGeneralLedgerAccount(false);
        // })
        // .catch(error => {
        //     AppModule.handleApiError(error);
        //     this.setLoadingGeneralLedgerAccount(false);
        // });
    }

    @Action
    updateAccountStateFilter(accountStateFilter: GeneralLedgerAccountState) {
        this.context.commit("setFilterByAccountState", accountStateFilter);
    }

    @Action
    clearAllFilters() {
        this.context.commit("setFilterByAccountState", null);
    }

    @Action
    cleanState() {
        this.context.commit("resetState");
    }

    @Mutation
    resetState() {
        this.loadingGeneralLedgerAccounts = true;
        this.loadingGeneralLedgerAccount = false;
        this.loadingNewGeneralLedgerAccount = false;
        this.generalLedgerAccounts = [];
        this.generalLedgerAccountsForDialog = [];
        this.totalGeneralLedgerAccountsPages = 1;
        this.generalLedgerAccount = new GeneralLedgerAccount();
        this.newGeneralLedgerAccountID = null;
        this.generalLedgerAccountJournalEntries = [];
        this.totalGeneralLedgerAccountJournalEntriesPages = 1;
        this.filterByAccountState = null;
    }

    @Mutation
    private async setLoadingGeneralLedgerAccounts(loading: boolean) {
        this.loadingGeneralLedgerAccounts = loading;
    }

    @Mutation
    private async setLoadingGeneralLedgerAccount(loading: boolean) {
        this.loadingGeneralLedgerAccount = loading;
    }

    @Mutation
    private async setLoadingNewGeneralLedgerAccount(loading: boolean) {
        this.loadingNewGeneralLedgerAccount = loading;
    }

    @Mutation
    private async setGeneralLedgerAccounts(generalLedgerAccounts: GeneralLedgerAccount[]) {
        this.generalLedgerAccounts = generalLedgerAccounts;
    }

    @Mutation
    private async setGeneralLedgerAccountsForDialog(generalLedgerAccounts: GeneralLedgerAccount[]) {
        this.generalLedgerAccountsForDialog = generalLedgerAccounts;
    }

    @Mutation
    private async setGeneralLedgerAccountsTotalPages(total: number) {
        this.totalGeneralLedgerAccountsPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setGeneralLedgerAccount(generalLedgerAccount: GeneralLedgerAccount) {
        this.generalLedgerAccount = generalLedgerAccount;
    }

    @Mutation
    private async setNewGeneralLedgerAccountID(id: string) {
        this.newGeneralLedgerAccountID = id;
    }

    @Mutation
    private async setGeneralLedgerAccountJournalEntries(journalEntries: JournalEntry[]) {
        this.generalLedgerAccountJournalEntries = journalEntries;
    }

    @Mutation
    private async setGeneralLedgerAccountJournalEntriesTotalPages(total: number) {
        this.totalGeneralLedgerAccountJournalEntriesPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setFilterByAccountState(accountState: GeneralLedgerAccountState) {
        this.filterByAccountState = accountState;
    }

}

export const GeneralLedgerAccountsModule = getModule(GeneralLedgerAccountsStore);